import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import { useRouter } from "next/router";
import { DROP_SVG } from "../../../../constants/svgConstants";
import { useTranslation } from "next-i18next";
import { useToggle } from "../../../../hooks";
import { useOnClickOutside } from "../../../../hooks/useOnClickOutside";
import { i18n } from "../../../../../next-i18next.config";
import { useLocalStorage } from "../../../../hooks/";
import { GET_CITIES_SLUG } from "../../../../graphql/queries/queriesCity";
import { GET_CLUB_HEADER_DATA } from "../../../../graphql/queries/queriesClub";
import { UPDATE_PROFILE } from "../../../../graphql/mutations/mutationClub";
import { useAuth } from "../../../../utils/auth";
import { useMutation, useQuery } from "@apollo/client";

const LANGUAGES = [
  {
    slug: "ru",
    title: "РУС",
  },
  {
    slug: "uk",
    title: "УКР",
  },
  {
    slug: "en",
    title: "ENG",
  },
  {
    slug: "kz",
    title: "ҚАЗ",
  },
  {
    slug: "ka",
    title: "GEO",
  },
  {
    slug: "pl",
    title: "POL",
  },
  {
    slug: "es",
    title: "ESP",
  },
  {
    slug: "de",
    title: "DEU",
  },
  {
    slug: "tr",
    title: "TUR",
  },
];

const NavBarLanguage = memo(({ setShowTabletMenu }) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { locales } = i18n;

  const router = useRouter();
  const { pathname, query, asPath } = router;
  const { country, club } = query;

  const { isAuthorized } = useAuth();

  const { data: { country: { languageDefault: languageCountryDefault, languages: languagesCountry = [] } = {} } = {} } = useQuery(
    GET_CITIES_SLUG,
    {
      variables: { slug: country, language },
      skip: !country,
    }
  );

  const { data: { club: { languageDefault: languageClubDefault, languages: languagesClub = [] } = {} } = {} } = useQuery(
    GET_CLUB_HEADER_DATA,
    {
      variables: { showOffer: false },
      skip: !club,
    }
  );

  const languages = !!languagesCountry.length ? languagesCountry : !!languagesClub.length ? languagesClub : [];

  const languageDefault = languageCountryDefault ?? languageClubDefault;

  const hasLanguages = !!languages.length;

  const ALL_LANGUAGES = useMemo(() => {
    // const allLanguages = [];

    return (hasLanguages ? languages : locales).reduce((acc, la) => {
      const newLang = LANGUAGES.find((lang) => la === lang.slug);
      return newLang ? acc.concat(newLang) : acc;
    }, []);

    // for (const lang of LANGUAGES)
    //   if (locales.includes(lang.slug)) allLanguages.push(lang);

    // return allLanguages;
  }, [LANGUAGES, locales, hasLanguages, languages]);

  const [showDropMenu, toggleDropMenu, hideDropMenu] = useToggle();

  const ref = useRef();
  useOnClickOutside(ref, hideDropMenu);

  const defaultLanguage = ALL_LANGUAGES.find(({ slug }) => slug === language)?.slug || ALL_LANGUAGES.at(0).slug;
  const selectedDefaultLanguage = ALL_LANGUAGES.find(({ slug }) => slug === languageDefault)?.slug || ALL_LANGUAGES.at(0).slug;
  const pickedLanguageTitle = ALL_LANGUAGES.find(({ slug }) => slug === defaultLanguage)?.title;

  const [localLanguage, setLocaleLanguage] = useLocalStorage(
    defaultLanguage, // language,
    "_language",
    language === (hasLanguages ? selectedDefaultLanguage : i18n.defaultLocale) //languageDefault
  );

  const [updateProfile] = useMutation(UPDATE_PROFILE);

  useEffect(() => {
    if (!localLanguage || language === localLanguage) return;

    if (hasLanguages && !languages.includes(localLanguage)) {
      setLocaleLanguage(defaultLanguage); //languageDefault
      return;
    } else if (!locales.includes(localLanguage)) {
      //!hasLanguages &&
      setLocaleLanguage(i18n.defaultLocale);
      return;
    }

    if (!hasLanguages && (country || club)) return;

    setShowTabletMenu();

    if (isAuthorized) {
      updateProfile({
        variables: { language: localLanguage },
        onError: () => null,
      });
    }

    router.push({ pathname, query }, asPath, { locale: localLanguage });

    return;
  }, [pathname, query, asPath, localLanguage, hasLanguages, languages, defaultLanguage, isAuthorized]);

  const changeLanguage = useCallback(
    (lang) => {
      setLocaleLanguage(lang);
      toggleDropMenu();
    },
    [pathname, query, asPath, setLocaleLanguage]
  );

  return (
    <li className={`navbar-search-item dropdown-language-button`} onClick={(e) => e.stopPropagation()} ref={ref}>
      {locales.length > 1 ? (
        <div
          className={`dropdown-button dropdown-button__language ${showDropMenu ? "dropdown-button_opened" : ""}`}
          onClick={toggleDropMenu}
        >
          {pickedLanguageTitle}
          <span className="dropdown-button__drop-svg">{DROP_SVG}</span>
        </div>
      ) : (
        <div className={`dropdown-button dropdown-button__language ${showDropMenu ? "dropdown-button_opened" : ""}`}>
          {pickedLanguageTitle}
        </div>
      )}
      {showDropMenu && (
        <div className="dropdown-language-menu" aria-label="dropdownMenuButton">
          {ALL_LANGUAGES.filter(({ slug }) => slug !== defaultLanguage).map(({ title, slug }) => (
            <span key={slug} className="dropdown-language-item" onClick={() => changeLanguage(slug)}>
              {title}
            </span>
          ))}
        </div>
      )}
    </li>
  );
});

NavBarLanguage.propTypes = {
  setShowTabletMenu: PropTypes.func,
};

NavBarLanguage.defaultProps = {
  setShowTabletMenu: () => {},
};

export default NavBarLanguage;
